// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Close run info bar component.
 *
 * Displays metadata about a close run in a compact horizontal bar,
 * including the close run key, creation time, period, and user who created it.
 * Used across various accounting workflow views to provide context about
 * the current close run.
 */
import { formatPersonName } from '@/account/utilities/format-person-name';
import { Paper, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';

/**
 * Person information for display.
 * Expects firstName and lastName as strings (can be empty).
 */
interface PersonInfo {
  firstName: string;
  lastName: string;
  key: string;
}

/**
 * Period information for display.
 */
interface PeriodInfo {
  year: number;
  month: number;
}

/**
 * Props for the CloseRunInfoBar component.
 */
interface CloseRunInfoBarProps {
  /** The unique key identifier for the close run */
  closeRunKey: string;
  /** When the close run was created */
  createdAt: DateTime;
  /** The accounting period for this close run */
  period: PeriodInfo;
  /** The user who created the close run */
  createdBy: PersonInfo;
}

/**
 * Formats a period for display.
 * Format: "YYYY mmmm" (e.g., "2024 September")
 */
function formatPeriod(period: PeriodInfo): string {
  const periodStartDate = new Date(period.year, period.month - 1, 1);
  return periodStartDate.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Displays close run metadata in a horizontal info bar.
 *
 * Shows the close run key, creation timestamp, period, and creating user
 * in a consistent format used across accounting workflow views.
 */
export function CloseRunInfoBar(props: CloseRunInfoBarProps) {
  return (
    <Paper sx={{ py: 1, px: 2 }}>
      <Stack direction="row" gap={3} justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
          Workflow ID: <strong>{props.closeRunKey}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Created:{' '}
          <strong>
            {props.createdAt.toFormat('MMM d, yyyy @ h:mm a ZZZZ')}
          </strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Period: <strong>{formatPeriod(props.period)}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          User: <strong>{formatPersonName(props.createdBy)}</strong>
        </Typography>
      </Stack>
    </Paper>
  );
}
